import { useState } from 'react';
import './ServiceWizard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SERVICES = [
  {
    key: 'Website Development',
    icon: '💻',
    desc: 'Custom, responsive & high-performance websites',
    types: [
      'Business Website',
      'eCommerce Store',
      'Web App / Portal',
      'Website Redesign',
    ],
  },
  {
    key: 'Digital Marketing',
    icon: '📈',
    desc: 'SEO, social media & paid ad campaigns that convert',
    types: [
      'SEO (Search Engine Optimization)',
      'Social Media Marketing',
      'Google / Meta Ads',
      'Content & Email Marketing',
    ],
  },
  {
    key: 'Graphic Design',
    icon: '🎨',
    desc: 'Logos, branding & creatives that stand out',
    types: [
      'Logo & Brand Identity',
      'Social Media Creatives',
      'Print Design (Brochure / Flyer)',
      'UI/UX Design',
    ],
  },
  {
    key: 'AI Solutions',
    icon: '🤖',
    desc: 'Smart automation & AI-powered business tools',
    types: [
      'AI Chatbot',
      'Process Automation',
      'AI Analytics & Insights',
      'Custom AI Integration',
    ],
  },
  {
    key: 'Custom Software Development',
    icon: '🛠️',
    desc: 'Bespoke software & tools built around your workflow',
    types: [
      'Custom Web Application',
      'Internal Tool / Dashboard',
      'API / Third-Party Integration',
      'SaaS Product Development',
    ],
  },
];

const TIMELINES = [
  { key: 'ASAP (within a week)',  icon: '⚡' },
  { key: '2–4 weeks',             icon: '📅' },
  { key: '1–2 months',            icon: '🗓️' },
  { key: 'Flexible / Not urgent', icon: '🌱' },
];

export default function ServiceWizard({ open, onClose }) {
  const [step,     setStep]     = useState(1);
  const [service,  setService]  = useState(null);
  const [type,     setType]     = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [phone,    setPhone]    = useState('');
  const [company,  setCompany]  = useState('');
  const [desc,     setDesc]     = useState('');
  const [status,   setStatus]   = useState('idle'); // idle | sending | success | error

  if (!open) return null;

  function reset() {
    setStep(1); setService(null); setType(null); setTimeline(null);
    setName(''); setEmail(''); setPhone(''); setCompany(''); setDesc(''); setStatus('idle');
  }

  function handleClose() {
    reset();
    onClose();
  }

  function pickService(s) {
    setService(s);
    setType(null);
    setStep(2);
  }

  function pickType(t) {
    setType(t);
    setStep(3);
  }

  function pickTimeline(t) {
    setTimeline(t);
    setStep(4);
  }

  async function handleComplete(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !phone.trim() || !desc.trim()) return;
    setStatus('sending');
    try {
      const r = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          phone,
          email,
          service,
          message:
            `Service enquiry via banner wizard.\n` +
            `Service: ${service}\nType needed: ${type}\nTimeline: ${timeline}\n` +
            `Company: ${company || '—'}\n\n` +
            `Project Description:\n${desc}`,
          source: 'wizard',
        }),
      });
      if (!r.ok) throw new Error('failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  const activeService = SERVICES.find(s => s.key === service);
  const totalSteps = 3;

  return (
    <div className="sw-overlay" onClick={handleClose}>
      <div className="sw-modal" onClick={e => e.stopPropagation()}>
        <button className="sw-close" onClick={handleClose} aria-label="Close">✕</button>

        {status !== 'success' && (
          <div className="sw-progress">
            {[1, 2, 3].map(n => (
              <div key={n} className={`sw-progress-step ${step >= n ? 'active' : ''} ${step > n ? 'done' : ''}`}>
                <span className="sw-progress-dot">{step > n ? '✓' : n}</span>
                <span className="sw-progress-label">
                  {n === 1 ? 'Service' : n === 2 ? 'Type' : 'Timeline'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ── STEP 1 — pick a service ─────────────────────────────────── */}
        {step === 1 && (
          <div className="sw-body">
            <h3 className="sw-title">What service do you need?</h3>
            <p className="sw-subtitle">Pick one to get started</p>
            <div className="sw-grid">
              {SERVICES.map(s => (
                <button key={s.key} className="sw-option-card" onClick={() => pickService(s.key)}>
                  <span className="sw-option-icon">{s.icon}</span>
                  <span className="sw-option-title">{s.key}</span>
                  <span className="sw-option-desc">{s.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 2 — pick a type within the chosen service ──────────── */}
        {step === 2 && activeService && (
          <div className="sw-body">
            <button className="sw-back" onClick={() => setStep(1)}>← Back</button>
            <h3 className="sw-title">{activeService.icon} {activeService.key}</h3>
            <p className="sw-subtitle">What type of {activeService.key.toLowerCase()} do you need?</p>
            <div className="sw-grid sw-grid-list">
              {activeService.types.map(t => (
                <button key={t} className="sw-option-row" onClick={() => pickType(t)}>
                  <span>{t}</span>
                  <span className="sw-arrow">→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 3 — timeline, then contact details + complete ──────── */}
        {step === 3 && (
          <div className="sw-body">
            <button className="sw-back" onClick={() => setStep(2)}>← Back</button>
            <h3 className="sw-title">⏱️ How soon do you need this done?</h3>
            <p className="sw-subtitle">Choose the timeline that fits you best</p>
            <div className="sw-grid">
              {TIMELINES.map(t => (
                <button
                  key={t.key}
                  className={`sw-option-card sw-option-card-sm ${timeline === t.key ? 'selected' : ''}`}
                  onClick={() => pickTimeline(t.key)}
                >
                  <span className="sw-option-icon">{t.icon}</span>
                  <span className="sw-option-title">{t.key}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 4 — contact details + complete ─────────────────────── */}
        {step === 4 && status !== 'success' && (
          <div className="sw-body">
            <button className="sw-back" onClick={() => setStep(3)}>← Back</button>
            <h3 className="sw-title">Tell us about yourself</h3>
            <p className="sw-subtitle">We'll use this information to get in touch with you</p>

            <div className="sw-summary">
              <div><span>Service</span><strong>{service}</strong></div>
              <div><span>Type</span><strong>{type}</strong></div>
              <div><span>Timeline</span><strong>{timeline}</strong></div>
            </div>

            <form className="sw-form" onSubmit={handleComplete}>
              <div className="sw-form-row">
                <div className="sw-field">
                  <label>Full Name <span className="sw-required">*</span></label>
                  <input
                    type="text" placeholder="John Doe" value={name}
                    onChange={e => setName(e.target.value)} required
                  />
                </div>
                <div className="sw-field">
                  <label>Email Address <span className="sw-required">*</span></label>
                  <input
                    type="email" placeholder="john@example.com" value={email}
                    onChange={e => setEmail(e.target.value)} required
                  />
                </div>
              </div>

              <div className="sw-form-row">
                <div className="sw-field">
                  <label>Phone Number <span className="sw-required">*</span></label>
                  <input
                    type="tel" placeholder="+91 1234567890" value={phone}
                    onChange={e => setPhone(e.target.value)} required
                  />
                </div>
                <div className="sw-field">
                  <label>Company Name (Optional)</label>
                  <input
                    type="text" placeholder="Your Company" value={company}
                    onChange={e => setCompany(e.target.value)}
                  />
                </div>
              </div>

              <div className="sw-field">
                <label>Project Description <span className="sw-required">*</span></label>
                <textarea
                  placeholder="Tell us about your project, requirements, goals, and any specific features you need..."
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  rows={4}
                  required
                />
              </div>

              {status === 'error' && <p className="sw-error">⚠️ Something went wrong. Please try again.</p>}
              <button type="submit" className="sw-complete-btn" disabled={status === 'sending'}>
                {status === 'sending' ? 'Submitting...' : '✅ Complete'}
              </button>
            </form>
          </div>
        )}

        {/* ── SUCCESS ──────────────────────────────────────────────────── */}
        {status === 'success' && (
          <div className="sw-body sw-success">
            <div className="sw-success-icon">✅</div>
            <h3 className="sw-title">Thank you, {name.split(' ')[0]}!</h3>
            <p className="sw-subtitle">
              We've got your {service} request for {type.toLowerCase()}, timeline: {timeline}.
              Our team will reach out to you shortly.
            </p>
            <button className="sw-complete-btn" onClick={handleClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
